import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Search, ChevronLeft, ChevronRight, Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const SUB_STATUS_CONFIG = {
  trial: { label: 'Trial', color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30', icon: Clock },
  active: { label: 'Active', color: 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30', icon: CheckCircle2 },
  grace_period: { label: 'Grace', color: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30', icon: AlertTriangle },
  expired: { label: 'Expired', color: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground border-border', icon: XCircle },
};

const PLAN_COLOR = {
  starter: 'bg-muted text-muted-foreground',
  professional: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  growth: 'bg-primary/10 text-primary',
  scale: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
};

function SubStatusBadge({ status }) {
  const cfg = SUB_STATUS_CONFIG[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium', cfg.color)}>
      <Icon size={9} /> {cfg.label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function TablePagination({ page, totalPages, totalItems, pageSize, onPageChange }) {
  if (totalItems === 0) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 px-3 py-2.5">
      <p className="text-[11px] text-muted-foreground">Showing {start}–{end} of {totalItems}</p>
      <div className="flex items-center gap-1">
        <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[4.5rem] text-center text-xs tabular-nums text-muted-foreground">{page} / {totalPages}</span>
        <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function CompaniesTableSection({
  companies, pageSize = 5, title = 'Companies', description, showViewAllLink = false, className,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subStatusFilter, setSubStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return companies.filter((c) => {
      if (statusFilter === 'active' && !c.isActive) return false;
      if (statusFilter === 'inactive' && c.isActive) return false;
      if (subStatusFilter !== 'all' && c.subscription?.status !== subStatusFilter) return false;
      if (!q) return true;
      return (
        c.companyName?.toLowerCase().includes(q) ||
        c.companyId?.toLowerCase().includes(q) ||
        c.contactEmail?.toLowerCase().includes(q) ||
        c.plan?.name?.toLowerCase().includes(q)
      );
    });
  }, [companies, search, statusFilter, subStatusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / pageSize));
  const paginatedCompanies = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredCompanies.slice(start, start + pageSize);
  }, [filteredCompanies, page, pageSize]);

  useEffect(() => { setPage(1); }, [search, statusFilter, subStatusFilter, pageSize]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const desc = description ?? `${filteredCompanies.length} of ${companies.length} tenant${companies.length === 1 ? '' : 's'}`;

  const goToDetail = (companyId) => navigate(`/super-admin/companies/${companyId}`);

  return (
    <Card className={cn('overflow-hidden rounded-md border-border shadow-sm', className)}>
      <CardHeader className="space-y-3 border-b border-border/60 bg-muted/10 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
            <CardDescription className="text-xs">{desc}</CardDescription>
          </div>
          {showViewAllLink && (
            <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
              <Link to="/super-admin/companies">View all</Link>
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center flex-wrap">
          <div className="relative min-w-0 flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, ID, email, plan…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 text-sm" />
          </div>
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full sm:w-auto">
            <TabsList className="grid h-9 w-full grid-cols-3 sm:w-auto">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              <TabsTrigger value="active" className="text-xs">Active</TabsTrigger>
              <TabsTrigger value="inactive" className="text-xs">Inactive</TabsTrigger>
            </TabsList>
          </Tabs>
          <select value={subStatusFilter} onChange={e => setSubStatusFilter(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring">
            <option value="all">All subscriptions</option>
            <option value="trial">Trial</option>
            <option value="active">Active</option>
            <option value="grace_period">Grace Period</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table className="text-xs [&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2">
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead>Company</TableHead>
              <TableHead className="hidden sm:table-cell">ID</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead className="hidden md:table-cell">Trial / Renewal</TableHead>
              <TableHead>Users</TableHead>
              <TableHead className="hidden md:table-cell">Sessions</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">No companies match your filters.</TableCell>
              </TableRow>
            ) : (
              paginatedCompanies.map((company) => {
                const sub = company.subscription || {};
                const planSlug = company.plan?.slug || sub.planSlug;
                const trialEnd = sub.trialEndDate ? formatDate(sub.trialEndDate) : null;
                const periodEnd = sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : null;
                const dateLabel = sub.status === 'trial' ? trialEnd : periodEnd;

                return (
                  <TableRow
                    key={company._id}
                    className="cursor-pointer transition-colors hover:bg-muted/40"
                    onClick={() => goToDetail(company.companyId)}
                  >
                    <TableCell>
                      <p className="font-medium text-foreground">{company.companyName}</p>
                      <p className="truncate text-[11px] text-muted-foreground sm:hidden">{company.companyId}</p>
                    </TableCell>
                    <TableCell className="hidden font-mono text-[11px] sm:table-cell">{company.companyId}</TableCell>
                    <TableCell>
                      {company.plan ? (
                        <span className={cn('px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize', PLAN_COLOR[planSlug] || 'bg-muted text-muted-foreground')}>
                          {company.plan.name}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <SubStatusBadge status={sub.status} />
                    </TableCell>
                    <TableCell className="hidden tabular-nums md:table-cell text-muted-foreground">{dateLabel || '—'}</TableCell>
                    <TableCell className="tabular-nums">{company.statistics?.totalUsers ?? 0}</TableCell>
                    <TableCell className="hidden tabular-nums md:table-cell">{company.statistics?.totalSessions ?? 0}</TableCell>
                    <TableCell>
                      <Badge variant={company.isActive ? 'default' : 'secondary'} className="text-[10px] font-normal">
                        {company.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={e => { e.stopPropagation(); goToDetail(company.companyId); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} totalItems={filteredCompanies.length} pageSize={pageSize} onPageChange={setPage} />
      </CardContent>
    </Card>
  );
}
